import { Component, Injector, OnInit } from '@angular/core';
import { paymentI } from 'app/core/interfaces/payment';
import { AuthService } from 'app/core/http/services/auth.service';
import { DialogService } from 'app/services/dialog.service';
import { PaymentDetailsComponent } from './payment-details/payment-details.component';
import { PaymentMethod, PaymentSettings } from 'app/core/interfaces/payment-settings';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentSettingsService } from 'app/core/http/services/payment-settings.service';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { getHttpErrorMessage } from 'app/core/utils/http-error-message';

@Component({
  selector: 'page-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
})
export class PaymentComponent extends BaseComponent implements OnInit {
  public paymentSettings!: PaymentSettings;
  public defaultPaymentMethod!: PaymentMethod[];
  public paymentForm: FormGroup;
  public daysBeforeEncounterForm: FormGroup;
  public mode = 'CREATE';

  public title = 'Configurações';

  public submittedPaymentSettings: boolean = false;
  public sucessPaymentSettings: boolean = false;

  bankDetailsForm: FormGroup;

  public descriptionCreditCard = 'Visa, Mastercard, Hipercard, Elo, American Express (Amex), Diners, Hiper, Cabal.';

  constructor(
    private dialogService: DialogService,
    private fb: FormBuilder,
    private paymentSettingsService: PaymentSettingsService,
    private injector: Injector,
  ) {
    super(injector);
    this.paymentForm = this.fb.group({
      PIXActive: [false, Validators.required],
      CreditCardActive: [false, Validators.required],
      CreditCardDescription: [{ value: '', disabled: true }, Validators.required],
      PhysicalActive: [false, Validators.required],
      secretary_commission_percentage: [0, [Validators.required, Validators.min(0), Validators.max(99), Validators.pattern(/^\d+$/)]],
    });

    this.bankDetailsForm = this.fb.group({
      bank_name: [null, Validators.required],
      agency_number: [null, Validators.required],
      account_number: ['', [Validators.required, Validators.pattern(/^\d{5,11}-\d$/)]],
      account_type: [null, Validators.required],
    });

    this.daysBeforeEncounterForm = this.fb.group({
      min_day_before_encounter: [1, Validators.required],
    });
  }

  ngOnInit(): void {
    this.getSettings();
  }

  get accountNumber() {
    return this.bankDetailsForm.get('account_number');
  }

  get mainPercentage(): number {
    const commission = this.paymentForm.get('secretary_commission_percentage')?.value;
    return 100 - (commission ?? 0);
  }

  getAllFormErrors(): Record<string, any> {
    const allErrors: Record<string, any> = {};

    for (const [key, control] of Object.entries(this.bankDetailsForm.controls)) {
      if (control.errors) {
        allErrors[key] = control.errors;
      }
    }

    return allErrors;
  }

  initializePaymentMethods(): void {
    this.defaultPaymentMethod = [
      {
        name: 'PIX',
        active: false,
        description: '',
      },
      {
        name: 'CREDIT_CARD',
        active: false,
        description: this.descriptionCreditCard,
      },
    ];
  }

  populatePaymentSettingsForm(paymentMethods: PaymentMethod[]) {
    const pixMethod = paymentMethods.find((method) => method.name === 'PIX');
    const creditCardMethod = paymentMethods.find((method) => method.name === 'CREDIT_CARD');
    const physicalMethod = paymentMethods.find((method) => method.name === 'PHYSICAL');

    this.paymentForm.patchValue({
      PIXActive: pixMethod ? pixMethod.active : false,
      CreditCardActive: creditCardMethod ? creditCardMethod.active : false,
      CreditCardDescription: creditCardMethod?.description ? creditCardMethod.description : this.descriptionCreditCard,
      PhysicalActive: physicalMethod ? physicalMethod.active : true,
    });
  }

  getSettings() {
    this.paymentSettingsService.findByOrganization(this.getUserLogged().id).subscribe({
      next: (paymentSettings: PaymentSettings) => {
        this.paymentSettings = paymentSettings;

        if (this.paymentSettings) {
          this.mode = 'UPDATE';

          this.populatePaymentSettingsForm(this.paymentSettings.payment_methods);
          this.paymentForm.patchValue({
            secretary_commission_percentage: this.paymentSettings.secretary_commission_percentage ?? 0,
          });
          this.daysBeforeEncounterForm.patchValue({
            min_day_before_encounter: this.paymentSettings.min_day_before_encounter,
          });

          const bank_account = this.paymentSettings.bank_account;

          this.bankDetailsForm.patchValue({
            bank_name: bank_account?.bank_name,
            agency_number: bank_account?.agency_number,
            account_number: bank_account?.account_number,
            account_type: bank_account?.account_type,
          });
        } else {
          this.initializePaymentMethods();
          this.mode = 'CREATE';
        }
      },
      error: (err: any) => {
        this.initializePaymentMethods();
        this.messageError = getHttpErrorMessage(err, 'Não foi possível carregar as configurações de pagamento. Tente novamente.');
        this.showErrorMessage = true;
        console.error(err);
      },
    });
  }

  createOrUpdateSettings() {
    if (this.mode == 'CREATE') {
      this.createSettings();
      return;
    }

    this.updateSettings();
  }

  createSettings() {
    const newPaymentSettings = {
      organization_id: this.getUserLogged().id,
      payment_methods: [
        {
          name: 'PIX',
          active: this.paymentForm.value.PIXActive,
          description: '',
        },
        {
          name: 'CREDIT_CARD',
          active: this.paymentForm.value.CreditCardActive,
          description: this.paymentForm.value.CreditCardDescription,
        },
      ],
      min_day_before_encounter: this.daysBeforeEncounterForm.value.min_day_before_encounter,
      secretary_commission_percentage: this.paymentForm.value.secretary_commission_percentage,
      bank_account: this.bankDetailsForm.value,
    };

    this.submittedPaymentSettings = true;

    this.paymentSettingsService.create(newPaymentSettings).subscribe({
      next: (paymentSettings: PaymentSettings) => {
        this.sucessPaymentSettings = true;
        this.paymentSettings = paymentSettings;
        this.mode = 'UPDATE';
      },
      error: (err: any) => {
        this.sucessPaymentSettings = false;
        this.messageError = getHttpErrorMessage(err, 'Não foi possível salvar as configurações de pagamento. Tente novamente.');

        console.error(err);
      },
    });
  }

  updateSettings() {
    const payment_methods = [
      {
        name: 'PIX',
        active: this.paymentForm.value.PIXActive,
        description: '',
      },
      {
        name: 'CREDIT_CARD',
        active: this.paymentForm.value.CreditCardActive,
        description: this.paymentForm.value.CreditCardDescription,
      },
    ];

    this.paymentSettings.payment_methods = payment_methods;
    ((this.paymentSettings.min_day_before_encounter = this.daysBeforeEncounterForm.value.min_day_before_encounter),
      (this.paymentSettings.secretary_commission_percentage = this.paymentForm.value.secretary_commission_percentage));
    this.paymentSettings.bank_account = this.bankDetailsForm.value;

    this.paymentSettingsService.update(this.paymentSettings, this.getUserLogged().id).subscribe({
      next: (paymentSettings: PaymentSettings) => {
        this.submittedPaymentSettings = true;
        this.sucessPaymentSettings = true;
        this.paymentSettings = paymentSettings;
      },
      error: (err: any) => {
        this.sucessPaymentSettings = false;
        this.submittedPaymentSettings = true;
        this.messageError = getHttpErrorMessage(err, 'Não foi possível atualizar as configurações de pagamento. Tente novamente.');
        console.error(err);
      },
    });
  }

  getPaymentMethod(name: string): PaymentMethod | undefined {
    return this.paymentSettings.payment_methods.find((method) => method.name === name);
  }

  openPaymentMethodDetail(method: paymentI) {
    const data = {
      title: method.name,
      param: { method },
    };

    this.dialogService.openComponentDialog(PaymentDetailsComponent, data);
  }
}
